import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Alert, Heading, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import { useTeamMeasuresQuery } from '../../hooks/queries/useWorkEnvironmentMeasuresQuery';
import { TrustpulseLogo } from '../../components/TrustpulseLogo';
import { voterToken } from '../../utils/voterToken';
import { questionLanguageFor } from '../FormSubmission/workEnvironmentFormView';
import { dayAndMonth } from '../WorkEnvironmentSurveyDetail/surveyProcessView';
import { PickForm } from './components/PickForm';
import { ProposeForm } from './components/ProposeForm';
import { teamMeasuresStep } from './teamMeasuresView';
import form from '../FormSubmission/WorkEnvironmentFormSubmission.module.css';
import classes from './WorkEnvironmentTeamMeasures.module.css';

const isNotFound = (error: unknown) => (error as { response?: { status?: number } } | null)?.response?.status === 404;

/** The team's link for proposing and pointing out measures */
export const WorkEnvironmentTeamMeasures = (): ReactElement => {
  const { t, i18n } = useTranslation();
  const { guid } = useParams<{ guid: string }>();
  const [token] = useState(() => voterToken(guid ?? ''));
  const { data, isLoading, error } = useTeamMeasuresQuery(guid, questionLanguageFor(i18n.language), token);

  const date = data?.deadline ? dayAndMonth(data.deadline, i18n.language) : null;
  const step = teamMeasuresStep(data?.step ?? null);

  return (
    <div className={form.page} data-size="md">
      <header className={form.header}>
        <div className={form.headerContent}>
          <TrustpulseLogo />
          {data && <span className={`ds-paragraph ${classes.teamName}`}>{data.team}</span>}
        </div>
      </header>
      <main id="main" className={form.main}>
        <div aria-hidden="true" className={form.backdrop} />
        <div className={form.content}>
          {isLoading && <Skeleton variant="rectangle" width="100%" height={320} />}
          {error && (
            <Alert data-color={isNotFound(error) ? 'info' : 'danger'}>
              <Paragraph>
                {t(isNotFound(error) ? 'workEnvironmentMeasures.team.notFound' : 'workEnvironmentMeasures.team.loadFailed')}
              </Paragraph>
            </Alert>
          )}
          {data && guid && (
            <>
              <div className={form.intro}>
                <span className={`ds-label ${form.eyebrow}`} data-size="sm">
                  {t(`workEnvironmentMeasures.team.eyebrow.${step}`, { title: data.title })}
                </span>
                <Heading level={1} data-size="xl" className={form.title}>
                  {t(`workEnvironmentMeasures.team.heading.${step}`)}
                </Heading>
                <Paragraph className={form.introText}>
                  {t(`workEnvironmentMeasures.team.intro.${step}`, {
                    date,
                    count: data.proposals.length,
                    max: data.maxHighlights,
                  })}
                </Paragraph>
                {step === 'highlight' && (
                  <Paragraph data-size="sm" className={form.subtle}>
                    {t('workEnvironmentMeasures.team.anonymousPicks')}
                  </Paragraph>
                )}
              </div>
              {step === 'propose' && <ProposeForm uuid={guid} measures={data} />}
              {step === 'highlight' && <PickForm uuid={guid} token={token} measures={data} />}
            </>
          )}
        </div>
      </main>
    </div>
  );
};
